import { ReadonlyVec2 } from 'gl-matrix';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';
import { Environment } from '~/engine/data/Environment';
import { RotationCursor } from '~/engine/data/common/customs/cursors/RotationCursor';
import { ToolCursor } from '~/engine/data/common/customs/cursors/SvgCursor';
import {
    TranslationCursor,
    TranslationCursorMode,
} from '~/engine/data/common/customs/cursors/TranslationCursor';
import { View } from '~/engine/view/View';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { MOUSE_BUTTON_LEFT } from '../MouseButton';
import { CrosshairInteractionMode, Tool } from '../Tool';
import { RotationAction } from '../actions/registration-actions/RotationAction';
import { TranslationAction } from '../actions/registration-actions/TranslationAction';

export enum RegistrationMode {
    TRANSLATION = 'translation',
    ROTATION = 'rotation',
    VOI = 'voi',
}
export class RegistrationTool extends Tool {
    public readonly toolType = 'registration';

    private readonly _translationAction: TranslationAction;
    private readonly _rotationAction: RotationAction;
    protected readonly _views: PlaneView[] | undefined;
    protected readonly _environment: Environment | undefined;
    private readonly _mode: RegistrationMode | null =
        RegistrationMode.TRANSLATION;
    private readonly _undoService: ReturnType<
        typeof getRegistrationUndoService
    >;
    private _cursorAction?: ToolCursor;

    public constructor(
        mode: RegistrationMode | null,
        views: PlaneView[] | undefined,
        environment: Environment | undefined
    ) {
        super();
        this._mode = mode;
        this._undoService = getRegistrationUndoService();
        this._translationAction = new TranslationAction(
            MOUSE_BUTTON_LEFT,
            this._undoService
        );
        this._rotationAction = new RotationAction(
            MOUSE_BUTTON_LEFT,
            this._undoService
        );
        this._views = views;
        this._environment = environment;
        this.initializeCursor();
    }

    private initializeCursor() {
        if (this._views) {
            this._cursorAction?.resetCursor();

            if (this._mode === RegistrationMode.TRANSLATION) {
                this._cursorAction = new TranslationCursor(this._views);
            } else if (this._mode === RegistrationMode.ROTATION) {
                this._cursorAction = new RotationCursor(this._views);
            }
        }
    }

    public get currentMode(): RegistrationMode | null {
        return this._mode;
    }

    /**
     * All registration modes show roots-only crosshairs between drags and
     * silently disable during a drag.  VOI mode never has a stroke in
     * progress (the tool returns EVENT_NOT_HANDLED for mouse events), so
     * it always returns RootsOnly via the same path as translation /
     * rotation between drags.
     */
    public override getCrosshairInteractionMode(
        _isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.gizmoCrosshairMode(this.isStrokeInProgress());
    }

    public override isActionInProgress(): boolean {
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.isActive;
        }
        return this._translationAction.isActive;
    }

    public override refreshCursor(): void {
        this._cursorAction?.setCustomCursor();
    }

    public onActivate(): EventHandlerResult {
        this.initializeCursor();
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.onMousePress(
                button,
                modifierKeys,
                coordinates,
                view,
                this._environment
            );
        }
        if (
            this._mode === RegistrationMode.TRANSLATION &&
            this._cursorAction instanceof TranslationCursor
        ) {
            this._cursorAction.setMode(TranslationCursorMode.GRAB);
        }
        return this._translationAction.onMousePress(
            button,
            modifierKeys,
            coordinates,
            view,
            this._environment
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._cursorAction) {
            this._cursorAction.setCustomCursor();
        } else {
            this.initializeCursor();
        }

        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.onMouseMove(
                coordinates,
                buttons,
                view,
                this._environment
            );
        }
        return this._translationAction.onMouseMove(
            coordinates,
            buttons,
            view,
            this._environment
        );
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2
    ): EventHandlerResult {
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const result =
            this._mode === RegistrationMode.ROTATION
                ? this._rotationAction.onMouseRelease(
                      button,
                      coordinates,
                      undefined,
                      this._environment
                  )
                : this._translationAction.onMouseRelease(
                      button,
                      coordinates,
                      undefined,
                      this._environment
                  );
        if (
            this._mode === RegistrationMode.TRANSLATION &&
            this._cursorAction instanceof TranslationCursor
        ) {
            this._cursorAction.setMode(TranslationCursorMode.DEFAULT);
            return result;
        }
        this._cursorAction?.setCustomCursor();
        return result;
    }

    public onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: View
    ): EventHandlerResult {
        if (key === 'Escape' && this.isActionInProgress()) {
            if (this._mode === RegistrationMode.ROTATION) {
                this._rotationAction.cancelAction(this._environment);
            } else if (this._mode === RegistrationMode.TRANSLATION) {
                this._translationAction.cancelAction(this._environment);
                if (this._cursorAction instanceof TranslationCursor) {
                    this._cursorAction.setMode(TranslationCursorMode.DEFAULT);
                }
            } else {
                throw new Error('Unknown registration mode');
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.onKeyPress(
                key,
                modifierKeys,
                view,
                this._environment
            );
        }
        return this._translationAction.onKeyPress(
            key,
            modifierKeys,
            view,
            this._environment
        );
    }

    public onDeactivate(): EventHandlerResult {
        this._cursorAction?.stopAction();
        this._cursorAction?.resetCursor();
        this._cursorAction = undefined;

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
